import { Locale } from "@redotech/crdb-enums/common/locale";
import {
  FLOW_ELEMENT_TRANSLATION_WRITE_LIMIT,
  FlowElementTranslationDelete,
  FlowElementTranslationWrite,
} from "@redotech/merchant-sdk/rpc/schema/return-flow-translations/set-flow-element-translations";
import { TRANSLATE_FLOW_ELEMENT_ENTRIES_LIMIT } from "@redotech/merchant-sdk/rpc/schema/return-flow-translations/translate-flow-element-text";
import { getLocaleName } from "@redotech/redo-i18n/locales";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { FLOW_ELEMENT_TRANSLATABLE_FIELDS } from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import { chunks } from "@redotech/util/iterator";
import {
  boundedSourceText,
  FlowTranslatableFieldSpec,
  normalizeTranslation,
} from "./translation-save-diff";

export const TRANSLATIONS_STALE_TIME = 5 * 60 * 1000;

/** One translatable piece of a step; `elementId` is absent until first save. */
export interface StepTranslationElement {
  elementId: string | undefined;
  field: FlowTranslatableFieldSpec;
}

export function stepElementIdsOf(
  elements: readonly StepTranslationElement[],
): string[] {
  return [
    ...new Set(
      elements.flatMap(({ elementId }) => (elementId ? [elementId] : [])),
    ),
  ];
}

export function flowStepTranslationsQueryKey(
  elementIds: readonly string[],
): readonly [string, string] {
  return ["flowStepTranslations", [...elementIds].sort().join(" ")];
}

/** A language is selected iff the step has at least one translation row for it. */
/** Sorted by name in `displayLocale`, the dashboard's language. */
export function selectedTranslationLanguages(
  translations: readonly { language: Locale }[],
  displayLocale: string,
): Locale[] {
  return [...new Set(translations.map(({ language }) => language))].sort(
    (a, b) =>
      getLocaleName(a, displayLocale).localeCompare(
        getLocaleName(b, displayLocale),
      ),
  );
}

interface StepTranslationEntry {
  elementId: string;
  field: FlowTranslatableFieldSpec;
  elementFieldKey: string;
  message: string;
}

export function stepTranslationEntries(
  elements: readonly StepTranslationElement[],
): StepTranslationEntry[] {
  const entries: StepTranslationEntry[] = [];
  for (const { elementId, field } of elements) {
    if (!elementId) {
      continue;
    }
    const message = normalizeTranslation(field.kind, field.source);
    if (!message) {
      continue;
    }
    entries.push({
      elementId,
      field,
      elementFieldKey: `${elementId}:${field.field}`,
      message,
    });
  }
  return entries;
}

export interface TranslationGenerationClient {
  translateFlowElementText(input: {
    entries: { key: string; message: string }[];
    targetLanguages: Locale[];
  }): Promise<{ translations: Record<string, Record<string, string>> }>;
  setFlowElementTranslations(input: {
    writes: FlowElementTranslationWrite[];
    deletes: FlowElementTranslationDelete[];
  }): Promise<unknown>;
}

/**
 * Persists nothing when translation yields no text, so a failed add never
 * leaves the language looking selected.
 */
export async function generateLanguageTranslations({
  client,
  elements,
  language,
  flowType,
}: {
  client: TranslationGenerationClient;
  elements: readonly StepTranslationElement[];
  language: Locale;
  flowType: FlowType;
}): Promise<{ untranslated: FlowTranslatableFieldSpec[] }> {
  const entries = stepTranslationEntries(elements);
  if (!entries.length) {
    throw new Error("This step has no text to translate yet.");
  }
  const translated = new Map<string, string>();
  for (const batch of chunks(entries, TRANSLATE_FLOW_ELEMENT_ENTRIES_LIMIT)) {
    const { translations } = await client.translateFlowElementText({
      entries: batch.map(({ elementFieldKey, message }) => ({
        key: elementFieldKey,
        message,
      })),
      targetLanguages: [language],
    });
    for (const [key, text] of Object.entries(translations[language] ?? {})) {
      translated.set(key, text);
    }
  }
  const writes: FlowElementTranslationWrite[] = [];
  const untranslated: FlowTranslatableFieldSpec[] = [];
  for (const entry of entries) {
    const text = translated.get(entry.elementFieldKey)?.trim();
    if (text) {
      writes.push({
        elementId: entry.elementId,
        field: entry.field.field,
        language,
        text,
        source: "ai",
        sourceText: boundedSourceText(entry.message),
        flowType,
      });
    } else {
      untranslated.push(entry.field);
    }
  }
  if (!writes.length) {
    throw new Error("Translation produced no text.");
  }
  for (const batch of chunks(writes, FLOW_ELEMENT_TRANSLATION_WRITE_LIMIT)) {
    await client.setFlowElementTranslations({ writes: batch, deletes: [] });
  }
  return { untranslated };
}

export interface SavedTranslationRow {
  elementId: string;
  field: string;
  language: Locale;
}

/** Deletes exactly the rows the step has saved for the language. */
export function languageRemovalDeletes(
  translations: readonly SavedTranslationRow[],
  language: Locale,
): FlowElementTranslationDelete[] {
  const deletes: FlowElementTranslationDelete[] = [];
  for (const row of translations) {
    if (row.language !== language) {
      continue;
    }
    const field = FLOW_ELEMENT_TRANSLATABLE_FIELDS.find(
      (candidate) => candidate === row.field,
    );
    if (field) {
      deletes.push({ elementId: row.elementId, field, language });
    }
  }
  return deletes;
}

export async function removeLanguageTranslations({
  client,
  translations,
  language,
}: {
  client: Pick<TranslationGenerationClient, "setFlowElementTranslations">;
  translations: readonly SavedTranslationRow[];
  language: Locale;
}): Promise<void> {
  const deletes = languageRemovalDeletes(translations, language);
  for (const batch of chunks(deletes, FLOW_ELEMENT_TRANSLATION_WRITE_LIMIT)) {
    await client.setFlowElementTranslations({ writes: [], deletes: batch });
  }
}
